import { __rest, __assign } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useEffect, useMemo, memo, forwardRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { createNamedContext, createNamedContextHook } from '@clover-util/react-context';
import { usePureState } from '@clover-util/use-pure-state';

function useApplyRefEffect(el, ref) {
    useEffect(function () {
        if (!ref || el === undefined)
            return;
        if (typeof ref === 'function') {
            ref(el);
        }
        else {
            ref.current = el;
        }
    }, [el, ref]);
}

function useClassNameEffect(el, className) {
    var classList = useMemo(function () { var _a; return (_a = className === null || className === void 0 ? void 0 : className.split(' ').filter(function (s) { return !!s; })) !== null && _a !== void 0 ? _a : []; }, [className]);
    useEffect(function () {
        var _a;
        if (!el)
            return;
        (_a = el.classList).add.apply(_a, classList);
        return function () {
            var _a;
            (_a = el.classList).remove.apply(_a, classList);
        };
    }, [classList, el]);
}

var startWithUpperCase = /^([A-Z]+).*/;
var startsWith$$ = /^\$\${1}.*/;
var REACT_RESERVED_FIELDS = new Set([
    'PropTypes',
    'childContextTypes',
    'compare',
    'contextType',
    'contextTypes',
    'defaultProps',
    'displayName',
    'getDefaultProps',
    'getDerivedStateFromProps',
    'name',
    'propTypes',
    'prototype',
    'tag',
    'type',
]);
function isReservedForReact(prop) {
    return startsWith$$.test(prop) || REACT_RESERVED_FIELDS.has(prop);
}
// eslint-disable-next-line @typescript-eslint/ban-types
function createLayout(renderer) {
    // debugging named context
    /* istanbul ignore next */
    var displayName = renderer.displayName || renderer.name || 'Layout';
    var RegionRegistry = createNamedContext("".concat(displayName, ".RegionRegistry"));
    var useRegionRegistry = createNamedContextHook('useRegionRegistry', RegionRegistry);
    function useRegionRef(regionName, _a) {
        var _b = _a === void 0 ? {} : _a, lazy = _b.lazy;
        if (isReservedForReact(regionName)) {
            throw new Error("".concat(displayName, ": Region ").concat(regionName, " is reserved for React."));
        }
        if (!startWithUpperCase.test(regionName)) {
            console.error("".concat(displayName, ": Rename region, ").concat(regionName, ", starting with an uppercase letter."));
        }
        var _c = useRegionRegistry(), registry = _c[0], mergeState = _c[1];
        var ref = useCallback(function (el) {
            var _a;
            return mergeState((_a = {}, _a[regionName] = el, _a));
        }, [regionName, mergeState]);
        return useMemo(function () {
            if (lazy && !Object.prototype.hasOwnProperty.call(registry, regionName))
                return;
            return ref;
        }, [lazy, ref, regionName, registry]);
    }
    var Template = function (props) { return renderer(props, useRegionRef); };
    Template.displayName = "".concat(displayName, ".Template");
    var Layout = memo(function (_a) {
        var children = _a.children, props = __rest(_a, ["children"]);
        var registry = usePureState({});
        return (jsxs(RegionRegistry.Provider, __assign({ value: registry }, { children: [jsx(Template, __assign({}, props)), children] })));
    });
    Layout.displayName = displayName;
    function createRegion(regionName) {
        var Region = memo(forwardRef(function Region(_a, ref) {
            var className = _a.className, children = _a.children;
            var _b = useRegionRegistry(), registry = _b[0], mergeState = _b[1];
            useEffect(function () { return mergeState(function (prev) {
                var _a;
                return Object.prototype.hasOwnProperty.call(prev, regionName)
                    ? {}
                    : (_a = {}, _a[regionName] = null, _a);
            }); }, [mergeState]);
            var el = registry[regionName];
            useApplyRefEffect(el, ref);
            useClassNameEffect(el, className);
            return el ? ReactDOM.createPortal(children, el) : null;
        }));
        Region.displayName = "".concat(displayName, ".").concat(regionName);
        return Region;
    }
    var regions = {};
    return new Proxy(Layout, {
        get: function (target, prop, receiver) {
            if (typeof prop === 'symbol' || isReservedForReact(prop)) {
                return Reflect.get(target, prop, receiver);
            }
            if (!regions[prop]) {
                regions[prop] = createRegion(prop);
            }
            return regions[prop];
        },
    });
}

export { REACT_RESERVED_FIELDS, createLayout };
